/**
 * Admin Locked Accounts Page
 * ==========================
 * View and manage locked accounts across the platform.
 * Super Admin can:
 * - View all currently locked accounts
 * - Unlock individual accounts
 * - View lockout history and details
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Lock,
  Unlock,
  RefreshCw,
  Search,
  AlertTriangle,
  Shield,
  Clock,
  MapPin,
  User
} from 'lucide-react';

const AdminLockedAccounts = () => {
  const { apiCall, isSuperAdmin } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [unlockDialog, setUnlockDialog] = useState({ open: false, email: null });

  useEffect(() => {
    fetchLockedAccounts();
  }, []);

  const fetchLockedAccounts = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/security/admin/lockouts');
      if (data.success) {
        setLockedAccounts(data.locked_accounts || []);
      }
    } catch (error) {
      console.error('Failed to fetch locked accounts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load locked accounts',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchEmail.trim()) {
      setSearchResult(null);
      return;
    }

    try {
      const data = await apiCall(`/api/security/admin/lockouts/${encodeURIComponent(searchEmail)}`);
      if (data.success) {
        setSearchResult(data.data);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to search account status',
        variant: 'destructive'
      });
    }
  };

  const handleUnlock = async (email) => {
    try {
      const data = await apiCall(`/api/security/admin/lockouts/${encodeURIComponent(email)}/unlock`, {
        method: 'POST'
      });
      if (data.success) {
        toast({
          title: 'Account Unlocked',
          description: data.message
        });
        fetchLockedAccounts();
        setSearchResult(null);
        setUnlockDialog({ open: false, email: null });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to unlock account',
        variant: 'destructive'
      });
    }
  };

  const formatTimeRemaining = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    return `${Math.floor(minutes / 1440)}d ${Math.floor((minutes % 1440) / 60)}h`;
  };

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                <span>Only Super Admins can access account lockout management.</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Lock className="w-7 h-7 text-red-600" />
              Locked Accounts
            </h1>
            <p className="text-slate-600 mt-1">
              Manage accounts locked due to failed login attempts
            </p>
          </div>
          <Button
            onClick={fetchLockedAccounts}
            variant="outline"
            disabled={loading}
            data-testid="refresh-lockouts-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Search Specific Account */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search Account Status</CardTitle>
            <CardDescription>
              Check lockout status for a specific email address
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="Enter email address..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                />
              </div>
              <Button type="submit" data-testid="search-lockout-btn">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>

            {searchResult && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-slate-400" />
                    <span className="font-medium">{searchResult.email}</span>
                  </div>
                  {searchResult.total_lockouts > 0 ? (
                    <Badge variant="destructive">
                      {searchResult.total_lockouts} Active Lockout(s)
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Not Locked
                    </Badge>
                  )}
                </div>

                {searchResult.lockouts && searchResult.lockouts.length > 0 && (
                  <div className="space-y-2">
                    {searchResult.lockouts.map((lockout, idx) => (
                      <div key={lockout.id || lockout.name || `${lockout.title || "lockout"}-${idx}`} className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="flex items-center gap-3">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{lockout.ip}</span>
                          {lockout.is_locked && (
                            <Badge variant="destructive" className="text-xs">
                              Locked
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-slate-500">
                          Lockout #{lockout.lockout_count}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchResult.total_lockouts > 0 && (
                  <Button
                    className="mt-3 w-full"
                    variant="default"
                    onClick={() => setUnlockDialog({ open: true, email: searchResult.email })}
                    data-testid="unlock-searched-account-btn"
                  >
                    <Unlock className="w-4 h-4 mr-2" />
                    Unlock This Account
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Currently Locked Accounts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Currently Locked Accounts ({lockedAccounts.length})
            </CardTitle>
            <CardDescription>
              Accounts temporarily locked due to too many failed login attempts
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-slate-100 rounded"></div>
                ))}
              </div>
            ) : lockedAccounts.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Shield className="w-12 h-12 mx-auto mb-3 text-green-300" />
                <p className="font-medium text-green-600">No Locked Accounts</p>
                <p className="text-sm">All accounts are currently accessible</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>Time Remaining</TableHead>
                    <TableHead>Lockout #</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lockedAccounts.map((account, idx) => (
                    <TableRow key={account.id || account.name || `${account.title || "account"}-${idx}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Lock className="w-4 h-4 text-red-500" />
                          <span className="font-medium">{account.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{account.ip}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-medium text-amber-600">
                            {formatTimeRemaining(account.remaining_minutes)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          #{account.lockout_count}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => setUnlockDialog({ open: true, email: account.email })}
                          data-testid={`unlock-${account.email}`}
                        >
                          <Unlock className="w-4 h-4 mr-1" />
                          Unlock
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">About Account Lockouts</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Accounts are locked after 5 failed login attempts</li>
                  <li>Lockout duration increases progressively: 15min → 30min → 1hr → 24hr</li>
                  <li>Users can reset their password to automatically unlock their account</li>
                  <li>Super Admins have additional recovery options (email link or OTP)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unlock Confirmation Dialog */}
        <AlertDialog open={unlockDialog.open} onOpenChange={(open) => setUnlockDialog({ open, email: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Unlock className="w-5 h-5 text-green-600" />
                Unlock Account?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will immediately unlock <strong>{unlockDialog.email}</strong> and allow them to log in.
                All lockouts for this email (from any IP) will be cleared.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleUnlock(unlockDialog.email)}
              >
                <Unlock className="w-4 h-4 mr-2" />
                Unlock Account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminLockedAccounts;
